import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { 
  FolderOpen, 
  ArrowLeft,
  FileText,
  ClipboardCheck,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  Download,
  RefreshCw,
  WifiOff,
  Wifi,
  ChevronRight,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { formatDuration } from '../lib/utils';

const OFFLINE_STORAGE_KEY = 'truckpro_onboard_documents';

export const DocumentsOnboardPage = () => {
  const navigate = useNavigate();
  const { get } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineData, setOfflineData] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  // Détecter le mode hors-ligne
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Charger les données depuis le cache local
  const loadOfflineData = useCallback(() => {
    try {
      const saved = localStorage.getItem(OFFLINE_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setOfflineData(parsed);
        return parsed;
      }
    } catch (e) {
      console.error('Error loading offline data:', e);
    }
    return null;
  }, []);

  // Sauvegarder les données pour le mode hors-ligne
  const saveOfflineData = useCallback((newData) => {
    try {
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify({
        ...newData,
        cached_at: new Date().toISOString()
      }));
      setOfflineData(newData);
    } catch (e) {
      console.error('Error saving offline data:', e);
    }
  }, []);

  // Récupérer les données du serveur
  const fetchData = useCallback(async (showToast = false) => {
    if (isOffline) {
      const cached = loadOfflineData();
      if (cached) {
        setData(cached);
        setLoading(false);
        if (showToast) toast.info('Mode hors-ligne: données du cache');
      }
      return;
    }

    setSyncing(true);
    try {
      const result = await get('/documents/onboard');
      if (result.success) {
        setData(result.data);
        saveOfflineData(result.data);
        if (showToast) toast.success('Documents synchronisés');
      }
    } catch (err) {
      // Fallback to offline data
      const cached = loadOfflineData();
      if (cached) {
        setData(cached);
        toast.warning('Erreur réseau - données du cache affichées');
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, [get, isOffline, loadOfflineData, saveOfflineData]);

  useEffect(() => {
    // Essayer de charger le cache d'abord pour un affichage rapide
    loadOfflineData();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getComplianceColor = (compliance) => {
    if (!compliance) return 'text-slate-400';
    const totalMissing = compliance.missing_reports.length + compliance.missing_inspections.length;
    if (totalMissing === 0) return 'text-green-500';
    if (totalMissing <= 3) return 'text-amber-500';
    return 'text-red-500';
  };

  const getComplianceIcon = (compliance) => {
    if (!compliance) return AlertTriangle;
    const totalMissing = compliance.missing_reports.length + compliance.missing_inspections.length;
    if (totalMissing === 0) return CheckCircle;
    if (totalMissing <= 3) return AlertTriangle;
    return XCircle;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const formatDateShort = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' });
  };

  if (loading && !offlineData) {
    return (
      <div className="page-wrapper p-4" data-testid="documents-loading">
        <div className="space-y-4">
          <div className="h-32 bg-slate-800 rounded-xl animate-pulse" />
          <div className="h-48 bg-slate-800 rounded-xl animate-pulse" />
          <div className="h-48 bg-slate-800 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  const displayData = data || offlineData;
  const compliance = displayData?.compliance;
  const ComplianceIcon = getComplianceIcon(compliance);

  return (
    <div className="page-wrapper" data-testid="documents-onboard-page">
      {/* Header */}
      <div className="app-header">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-slate-800 rounded-lg"
              data-testid="back-button"
            >
              <ArrowLeft className="text-slate-400" size={24} />
            </button>
            <div className="flex items-center gap-3">
              <FolderOpen className="text-amber-500" size={28} />
              <div>
                <h1 className="text-xl text-white">Documents utiles</h1>
                <p className="text-slate-400 text-sm">14 derniers jours (facultatif)</p>
              </div>
            </div>
          </div>
          
          {/* Sync Status */}
          <div className="flex items-center gap-2">
            {isOffline ? (
              <div className="flex items-center gap-1 text-amber-500 text-xs">
                <WifiOff size={14} />
                <span>Hors-ligne</span>
              </div>
            ) : (
              <button
                onClick={() => fetchData(true)}
                disabled={syncing}
                className="p-2 hover:bg-slate-800 rounded-lg"
                data-testid="sync-button"
              >
                <RefreshCw className={`text-slate-400 ${syncing ? 'animate-spin' : ''}`} size={20} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Compliance Banner */}
        <div 
          className={`rounded-xl p-4 border ${
            compliance?.is_compliant 
              ? 'bg-green-900/20 border-green-800' 
              : 'bg-amber-900/20 border-amber-800'
          }`}
          data-testid="compliance-banner"
        >
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              compliance?.is_compliant ? 'bg-green-500' : 'bg-amber-500'
            }`}>
              <ComplianceIcon className="text-black" size={28} />
            </div>
            <div className="flex-1">
              <p className={`font-bold text-lg ${getComplianceColor(compliance)}`}>
                {compliance?.is_compliant ? 'Documents a jour' : 'Documents a completer'}
              </p>
              <p className="text-slate-400 text-sm">
                {compliance?.reports_count || 0} rapports · {compliance?.inspections_count || 0} inspections
              </p>
            </div>
            <Shield className={getComplianceColor(compliance)} size={24} />
          </div>
          
          {!compliance?.is_compliant && (
            <div className="mt-3 pt-3 border-t border-slate-700">
              <p className="text-sm text-slate-300">
                <span className="text-amber-400 font-semibold">
                  {(compliance?.missing_reports?.length || 0) + (compliance?.missing_inspections?.length || 0)}
                </span> documents a completer sur les 14 derniers jours (facultatif)
              </p>
            </div>
          )}
        </div>

        {/* Offline Data Notice */}
        {offlineData && !data && (
          <div className="bg-blue-900/20 border border-blue-800 rounded-xl p-3 flex items-center gap-3">
            <WifiOff className="text-blue-400" size={20} />
            <div className="flex-1">
              <p className="text-blue-300 text-sm">
                Données du cache - {offlineData.cached_at ? new Date(offlineData.cached_at).toLocaleString('fr-CA') : 'Date inconnue'}
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 bg-slate-900 p-1 rounded-xl">
          {[
            { id: 'summary', label: 'Résumé' },
            { id: 'reports', label: 'Rapports' },
            { id: 'inspections', label: 'Inspections' },
            { id: 'hours', label: 'Heures' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'bg-amber-500 text-black' 
                  : 'text-slate-400 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'summary' && (
          <div className="space-y-4">
            {/* Period Info */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="text-amber-500" size={20} />
                <span className="text-white font-bold">Période obligatoire</span>
              </div>
              <p className="text-slate-400 text-sm">
                Du {displayData?.period?.start ? formatDate(displayData.period.start) : '-'} au {displayData?.period?.end ? formatDate(displayData.period.end) : '-'}
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                <FileText className="text-blue-500 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-white">{compliance?.reports_count || 0}</p>
                <p className="text-xs text-slate-500">Rapports</p>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                <ClipboardCheck className="text-green-500 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-white">{compliance?.inspections_count || 0}</p>
                <p className="text-xs text-slate-500">Inspections</p>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                <Clock className="text-amber-500 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-white">{compliance?.hours_days_count || 0}</p>
                <p className="text-xs text-slate-500">Jours heures</p>
              </div>
            </div>

            {/* Missing Documents Alert */}
            {compliance && (compliance.missing_reports.length > 0 || compliance.missing_inspections.length > 0) && (
              <div className="bg-red-900/20 border border-red-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <XCircle className="text-red-500" size={20} />
                  <span className="text-red-400 font-bold">Documents manquants</span>
                </div>
                
                {compliance.missing_reports.length > 0 && (
                  <div className="mb-3">
                    <p className="text-slate-400 text-sm mb-2">Rapports manquants:</p>
                    <div className="flex flex-wrap gap-2">
                      {compliance.missing_reports.slice(0, 7).map(date => (
                        <span key={date} className="px-2 py-1 bg-red-900/50 rounded text-xs text-red-300">
                          {formatDateShort(date)}
                        </span>
                      ))}
                      {compliance.missing_reports.length > 7 && (
                        <span className="px-2 py-1 bg-red-900/50 rounded text-xs text-red-300">
                          +{compliance.missing_reports.length - 7}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {compliance.missing_inspections.length > 0 && (
                  <div>
                    <p className="text-slate-400 text-sm mb-2">Inspections manquantes:</p>
                    <div className="flex flex-wrap gap-2">
                      {compliance.missing_inspections.slice(0, 7).map(date => (
                        <span key={date} className="px-2 py-1 bg-red-900/50 rounded text-xs text-red-300">
                          {formatDateShort(date)}
                        </span>
                      ))}
                      {compliance.missing_inspections.length > 7 && (
                        <span className="px-2 py-1 bg-red-900/50 rounded text-xs text-red-300">
                          +{compliance.missing_inspections.length - 7}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Info */}
            <div className="bg-slate-800/50 rounded-xl p-4">
              <p className="text-slate-400 text-sm">
                <strong className="text-white">Règlement SAAQ:</strong> Vous devez conserver à bord les fiches journalières et rapports d'inspection des 14 derniers jours. Conservation totale: 6 mois minimum.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="space-y-3">
            {displayData?.reports?.length > 0 ? (
              displayData.reports.map(report => (
                <button
                  key={report.id}
                  onClick={() => navigate(`/reports/${report.id}`)}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-4 hover:bg-slate-800 transition-colors text-left"
                  data-testid={`report-${report.id}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <FileText className="text-blue-500" size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{report.report_number || 'Rapport'}</p>
                    <p className="text-slate-400 text-sm">{formatDate(report.date)}</p>
                    {report.stops && (
                      <p className="text-slate-500 text-xs">{report.stops.length} arrêts</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {report.status === 'completed' ? (
                      <CheckCircle className="text-green-500" size={18} />
                    ) : (
                      <AlertTriangle className="text-amber-500" size={18} />
                    )}
                    <ChevronRight className="text-slate-600" size={20} />
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <FileText className="text-slate-600 mx-auto mb-3" size={48} />
                <p className="text-slate-400">Aucun rapport sur cette période</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'inspections' && (
          <div className="space-y-3">
            {displayData?.inspections?.length > 0 ? (
              displayData.inspections.map(inspection => (
                <div
                  key={inspection.id}
                  className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-4"
                  data-testid={`inspection-${inspection.id}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <ClipboardCheck className="text-green-500" size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">Inspection pré-départ</p>
                    <p className="text-slate-400 text-sm">{formatDate(inspection.date)}</p>
                    {inspection.odometer && (
                      <p className="text-slate-500 text-xs">{inspection.odometer.toLocaleString()} km</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {inspection.signature ? (
                      <CheckCircle className="text-green-500" size={18} />
                    ) : (
                      <AlertTriangle className="text-amber-500" size={18} />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <ClipboardCheck className="text-slate-600 mx-auto mb-3" size={48} />
                <p className="text-slate-400">Aucune inspection sur cette période</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'hours' && (
          <div className="space-y-3">
            {displayData?.service_hours?.length > 0 ? (
              displayData.service_hours.map(day => (
                <div
                  key={day.date}
                  className="bg-slate-900 border border-slate-800 rounded-xl p-4"
                  data-testid={`hours-${day.date}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-white font-medium">{formatDate(day.date)}</p>
                    <p className="text-amber-500 font-mono font-bold">
                      {formatDuration(day.total_work_min)}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-xs text-slate-500">Conduite</p>
                      <p className="text-white font-mono text-sm">{formatDuration(day.driving_min)}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-xs text-slate-500">Service</p>
                      <p className="text-white font-mono text-sm">{formatDuration(day.on_duty_min)}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-xs text-slate-500">Repos</p>
                      <p className="text-white font-mono text-sm">{formatDuration(day.off_duty_min)}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Clock className="text-slate-600 mx-auto mb-3" size={48} />
                <p className="text-slate-400">Aucune donnée d'heures sur cette période</p>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 pt-4">
          <Button
            onClick={() => navigate('/reports/new')}
            className="h-14 bg-blue-600 hover:bg-blue-700"
            data-testid="new-report-btn"
          >
            <FileText size={20} className="mr-2" />
            Nouveau rapport
          </Button>
          <Button
            onClick={() => navigate('/inspection')}
            className="h-14 bg-green-600 hover:bg-green-700"
            data-testid="new-inspection-btn"
          >
            <ClipboardCheck size={20} className="mr-2" />
            Inspection
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentsOnboardPage;
